import { router } from 'expo-router';
import { ArrowRight } from 'lucide-react-native';
import { Text as RNText, StyleSheet, View } from 'react-native';

import { BadgePill } from '@/components/BadgePill';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Heading } from '@/components/Heading';
import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { splitLeadingEmoji } from '@/lib/leadingEmoji';
import { formatPromoEndDate } from '@/lib/promoDate';
import type { BonusPromo } from '@/types/deal';

export interface BonusPromoCardProps {
  /** Server-owned banner payload — the card renders exactly what's here. */
  promo: BonusPromo;
  testID?: string;
}

/**
 * Double-payout promo banner for Home's pinned slot (Sept 2026 bonus
 * weeks). Everything on it is a server field: whether it renders at all
 * is `bonus_promo` being non-null on the deals index, so the kill switch
 * or the window end removes it with no OTA — same off-switch design as
 * SprintHomeCard before it.
 *
 * Deliberately built to SprintHomeCard/FeaturedDealCard geometry (Deep
 * Green surface, `radii['2xl']` corner, Electric Green hairline, same CTA
 * weight) because it takes their place in the slot while the boost runs.
 *
 * Like the sprint card, the whole card is NOT pressable — the Button is
 * the only tap target (see SprintHomeCard for why).
 */
export function BonusPromoCard({ promo, testID }: BonusPromoCardProps) {
  const boostedDollars = (promo.referrer_cut_cents / 100).toFixed(0);
  const baseDollars = (promo.base_referrer_cut_cents / 100).toFixed(0);
  // headline is authored per campaign (the server sends no banner without
  // one) and usually leads with an emoji. The emoji renders beside the
  // Heading, not inside it: sharing a line with FK Screamer clips the cap
  // tops on iOS (see lib/leadingEmoji). body blank → the rate sentence,
  // assembled from live amounts with the same was/now strikethrough as the
  // Refer hero, so it can never contradict what the boost actually pays.
  // Authored body renders as plain text — a server string can't carry
  // styling.
  const [emoji, headline] = splitLeadingEmoji(promo.headline);
  // Sub-dollar boosts can collide once floored to whole dollars — a strike
  // over an unchanged number reads as a glitch, so fall back to prose-free
  // boosted-only copy in that (unlikely) case.
  const showStrike = baseDollars !== boostedDollars;

  return (
    <Card variant="dark" padding={spacing.lg} style={styles.card} testID={testID}>
      <View style={styles.topRow}>
        <View style={styles.titleRow}>
          {/* Decorative: VoiceOver reads the Heading's words, not "high
              voltage sign". aria-hidden is the cross-platform prop (iOS
              accessibilityElementsHidden, web aria-hidden). */}
          {emoji ? (
            <RNText style={styles.titleEmoji} aria-hidden>
              {emoji}
            </RNText>
          ) : null}
          <Heading size="2xl" tone="inverse" style={styles.title}>
            {headline}
          </Heading>
        </View>
        <BadgePill label={promo.badge_label} testID="bonus-promo-badge" />
      </View>

      {promo.body != null ? (
        <Text variant="bodyMedium" color="frost" style={styles.rate}>
          {promo.body}
        </Text>
      ) : (
        <Text variant="bodyMedium" color="frost" style={styles.rate}>
          {`Every ${promo.display_name} referral pays `}
          {showStrike ? (
            <>
              <Text variant="bodyMedium" color="textOnDarkMuted" style={styles.strike}>
                {`$${baseDollars}`}
              </Text>
              {' '}
            </>
          ) : null}
          <Text variant="bodyMedium" color="frost" style={styles.boostedRate}>
            {`$${boostedDollars}`}
          </Text>
        </Text>
      )}
      {promo.ends_at ? (
        <Text variant="caption" color="textOnDarkMuted" style={styles.ends}>
          {`Ends ${formatPromoEndDate(promo.ends_at)}`}
        </Text>
      ) : null}

      <Button
        label="Share your link"
        trailingIcon={ArrowRight}
        fullWidth
        onPress={() => router.push('/referrals')}
        style={styles.cta}
        testID="bonus-promo-share-cta"
      />
    </Card>
  );
}

const styles = StyleSheet.create({
  // Mirrors SprintHomeCard.card / FeaturedDealCard.card — same slot, so a
  // different corner or border would read as the page jumping when one
  // swaps for the other.
  card: {
    borderRadius: radii['2xl'],
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.electricGreen,
    marginTop: spacing.sm,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 1,
  },
  // Sized to the 2xl Heading beside it; no lineHeight so the emoji keeps
  // its natural box instead of clipping itself.
  titleEmoji: {
    fontSize: fontSizes['2xl'],
  },
  title: {
    flexShrink: 1,
  },
  rate: {
    marginTop: spacing.md,
  },
  // Same was/now treatment as HeroEarningsCard: frost line through muted
  // digits, bold frost for the live rate.
  strike: {
    textDecorationLine: 'line-through',
    textDecorationColor: colors.frost,
  },
  boostedRate: {
    fontFamily: fonts.bodyBold,
  },
  ends: {
    marginTop: spacing.xs,
  },
  cta: {
    marginTop: spacing.lg,
  },
});
